import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Logo } from '@/components/Logo';
import { toast } from 'sonner';
import { Eye, EyeOff, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type Strength = {
  score: 0 | 1 | 2 | 3 | 4;
  suggestions: string[];
  hasCommonPattern: boolean;
};

const COMMON_PATTERNS = [
  /password/i,
  /qwerty/i,
  /12345/,
  /abcdef/i,
  /(.)\1{3,}/, // 4+ repeated chars
  /(?:0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|defg)/i,
];

function scorePassword(pw: string): Strength {
  const checks = {
    len8: pw.length >= 8,
    len12: pw.length >= 12,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const hasCommonPattern = pw.length > 0 && COMMON_PATTERNS.some((re) => re.test(pw));

  let raw = 0;
  if (checks.len8) raw++;
  if (checks.len12) raw++;
  if (checks.lower) raw++;
  if (checks.upper) raw++;
  if (checks.number) raw++;
  if (checks.symbol) raw++;

  // Map 0-6 raw points to 0-4 score
  let score: Strength['score'] = 0;
  if (raw >= 6) score = 4;
  else if (raw >= 5) score = 3;
  else if (raw >= 4) score = 2;
  else if (raw >= 2) score = 1;

  if (hasCommonPattern && score > 1) score = 1;

  const suggestions: string[] = [];
  if (!checks.len8) suggestions.push('Use at least 8 characters');
  if (!checks.lower) suggestions.push('Add a lowercase letter');
  if (!checks.upper) suggestions.push('Add an uppercase letter');
  if (!checks.number) suggestions.push('Add a number');
  if (!checks.symbol) suggestions.push('Add a symbol (!@#$…)');
  if (hasCommonPattern) suggestions.push("Avoid common patterns like '1234' or 'password'");
  // Soft nudge once everything else is met
  if (
    checks.len8 &&
    !checks.len12 &&
    checks.lower &&
    checks.upper &&
    checks.number &&
    checks.symbol &&
    !hasCommonPattern
  ) {
    suggestions.push('Use 12+ characters for extra strength');
  }

  return { score, suggestions, hasCommonPattern };
}

const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;
const STRENGTH_TEXT = [
  'text-destructive',
  'text-orange-500',
  'text-yellow-500',
  'text-lime-500',
  'text-green-500',
] as const;
const STRENGTH_BAR = [
  'bg-destructive',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-green-500',
] as const;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [weakPasswordError, setWeakPasswordError] = useState(false);

  const strength = useMemo(() => scorePassword(password), [password]);
  const passwordsMatch = confirm.length > 0 && password === confirm;
  const canSubmit =
    password.length >= 8 && strength.score >= 3 && passwordsMatch && !submitting;

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when the user lands here from the email link
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    // After 4s with no session established, treat the link as expired/used
    const timer = window.setTimeout(() => setExpired(true), 4000);
    return () => {
      sub.subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  // Once ready, never show the expired state
  useEffect(() => {
    if (ready) setExpired(false);
  }, [ready]);

  // Clear weak password error when password changes
  useEffect(() => {
    setWeakPasswordError(false);
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      return toast.error('Password must be at least 8 characters');
    }
    if (strength.score < 3) {
      setWeakPasswordError(true);
      return toast.error('Please choose a stronger password');
    }
    if (password !== confirm) {
      return toast.error('Passwords do not match');
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success('Password updated. Signing you in…');
    navigate('/dashboard');
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return toast.error('Enter your email');
    setResending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resendEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResending(false);
    if (error) return toast.error(error.message);
    toast.success('New reset link sent. Check your inbox.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <a href="/" aria-label="PullOver.App home" className="inline-block mx-auto mb-4">
            <Logo variant="full" className="h-12 object-contain mx-auto" />
          </a>
          <CardTitle className="font-display text-2xl">Reset your password</CardTitle>
          <CardDescription>
            {ready
              ? 'Choose a new password for your account'
              : expired
                ? 'Request a new reset link below'
                : 'Verifying your reset link…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ready ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">New password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={4}
                      aria-valuenow={strength.score}
                      aria-label="Password strength"
                      className="flex gap-1"
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={cn(
                            'h-1.5 flex-1 rounded-full bg-muted transition-colors',
                            i < strength.score && STRENGTH_BAR[strength.score],
                          )}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Strength</span>
                      <span className={cn('font-medium', STRENGTH_TEXT[strength.score])}>
                        {STRENGTH_LABELS[strength.score]}
                      </span>
                    </div>
                    <ul aria-live="polite" className="space-y-1 text-xs text-muted-foreground">
                      {strength.score === 4 ? (
                        <li className="flex items-center gap-1.5 text-green-500">
                          <Check className="h-3.5 w-3.5" />
                          Strong password — looks great
                        </li>
                      ) : (
                        strength.suggestions.map((s) => (
                          <li key={s} className="flex items-start gap-1.5">
                            <span aria-hidden className="mt-1 inline-block h-1 w-1 rounded-full bg-muted-foreground/60" />
                            <span>{s}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm password</label>
                <div className="relative">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirm.length > 0 && (
                  <p
                    className={cn(
                      'text-xs',
                      passwordsMatch ? 'text-green-500' : 'text-muted-foreground',
                    )}
                  >
                    {passwordsMatch ? 'Passwords match' : "Passwords don't match yet"}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={!canSubmit}>
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
              {weakPasswordError && strength.score < 3 && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
                  <p className="font-medium text-destructive mb-2">
                    Your password is too weak — it must be Strong or Very strong to continue
                  </p>
                  <ul className="space-y-1 text-destructive/90">
                    {strength.suggestions.map((s) => (
                      <li key={s} className="flex items-start gap-1.5">
                        <span className="mt-1.5 inline-block h-1 w-1 rounded-full bg-destructive/60" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          ) : expired ? (
            <form onSubmit={handleResend} className="space-y-4">
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                This link has expired or was already used. Enter your email to get a new one.
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  placeholder="you@dealership.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={resending}>
                {resending ? 'Sending…' : 'Send new reset link'}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full text-xs text-muted-foreground hover:text-foreground"
              >
                Back to sign in
              </button>
            </form>
          ) : (
            <div className="text-center text-sm text-muted-foreground py-6">
              Verifying your reset link…
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
